import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Sparkles, ArrowRight, Gift, Star, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentTitle } from "@/lib/use-document-title";
import { getStripeEnvironment } from "@/lib/stripe";
import { AccessIcon } from "@/components/AccessIcon";

type State = "syncing" | "ready" | "needs_login" | "needs_confirm";

function AccessValkommen() {
  useDocumentTitle("Välkommen — Lagamobilen Access");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState<State>("syncing");
  const [memberNumber, setMemberNumber] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30; // ~60 sek

    async function poll() {
      // Delad dator: om det redan finns en session i webbläsaren från en tidigare
      // kund/personal så logga ut först, annars "ärver" nya kunden fel identitet
      // och gammal data dyker upp i aktiveringsflödet. Vi identifierar den nya
      // kunden uteslutande via Stripe-sessionens e-post + magisk länk.
      if (sessionId) {
        try { await supabase.auth.signOut(); } catch { /* noop */ }
        try {
          // Rensa ev. formulärutkast från förra kunden.
          Object.keys(localStorage)
            .filter((k) => k.startsWith("access:draft:") || k.startsWith("joinflow:"))
            .forEach((k) => localStorage.removeItem(k));
        } catch { /* noop */ }
      }

      while (!cancelled && attempts < maxAttempts) {
        attempts++;

        // 1) Redan inloggade som rätt person (t.ex. reload efter aktivering)?
        //    Endast giltigt när vi INTE precis kom från checkout (då har vi
        //    just loggat ut ovan).
        if (!sessionId) {
          const { data: { session: existing } } = await supabase.auth.getSession();
          if (existing) {
            const { data: m } = await supabase
              .from("members")
              .select("first_name, member_number")
              .eq("user_id", existing.user.id)
              .maybeSingle();
            if (m && !cancelled) {
              navigate("/access/aktivera?from=checkout", { replace: true });
              return;
            }
          }
        }

        // 2) Be servern verifiera Stripe-sessionen och returnera en magisk
        //    inloggningslänk. Vi kan inte logga in lokalt eftersom kunden
        //    inte angett något lösenord ännu.
        if (sessionId) {
          try {
            const envName = getStripeEnvironment();
            const { data, error } = await supabase.functions.invoke("start-post-checkout-session", {
              body: { sessionId, environment: envName },
            });
            if (!error && data && !(data as any).pending) {
              const tokenHash = (data as any).token_hash as string | undefined;
              if (tokenHash) {
                const { data: verified } = await supabase.auth.verifyOtp({
                  type: "magiclink",
                  token_hash: tokenHash,
                });
                if (verified?.session) {
                  if (verified.user?.email) setEmail(verified.user.email);
                  navigate("/access/aktivera?from=checkout", { replace: true });
                  return;
                }
              }
            }
          } catch (e) {
            console.warn("[valkommen] post-checkout login failed", e);
          }
        }

        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) {
        const { data: { session } } = await supabase.auth.getSession();
        setState(session ? "ready" : "needs_login");
      }
    }
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--color-ink)] via-[#1a1a1a] to-[var(--color-ink)] text-white">
      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
        <div className="flex items-center gap-3 mb-10">
          <AccessIcon size={32} className="text-[var(--color-gold)]" />
          <div>
            <div className="font-display font-light text-white tracking-wider text-sm">LAGAMOBILEN</div>
            <div className="font-display font-semibold text-[var(--color-gold)] text-2xl tracking-[0.2em] leading-none">ACCESS</div>
          </div>
        </div>

        {state === "syncing" && (
          <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-10 text-center">
            <Loader2 className="mx-auto mb-5 animate-spin text-[var(--color-gold)]" size={40} />
            <h1 className="!text-3xl text-white mb-3">Slutför ditt medlemskap…</h1>
            <p className="text-white/70 text-sm max-w-md mx-auto">
              Betalningen är genomförd. Vi aktiverar nu ditt medlemskap och förbereder din portal. Det tar bara några sekunder.
            </p>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="rounded-3xl bg-gradient-to-br from-[var(--color-gold)]/10 to-transparent border border-[var(--color-gold)]/30 p-8 lg:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-display font-semibold mb-5">
                <CheckCircle2 size={14} /> Medlemskap aktiverat
              </div>
              <h1 className="!text-4xl text-white mb-3">
                Välkommen{firstName ? `, ${firstName}` : ""}!
              </h1>
              <p className="text-white/75 text-base leading-relaxed max-w-xl">
                Tack för att du blev medlem i Lagamobilen Access. Du har nu tillgång till alla förmåner, partnererbjudanden och event.
              </p>
              {memberNumber && (
                <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/[0.04] border border-white/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Medlemsnummer</div>
                    <div className="font-mono text-white text-lg">{memberNumber}</div>
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate("/access/dashboard")}
                className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white font-display font-semibold text-sm transition"
              >
                Gå till min portal <ArrowRight size={16} />
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <Link to="/access/formaner" className="group rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[var(--color-gold)]/40 hover:bg-white/[0.06] p-6 transition">
                <Star className="text-[var(--color-gold)] mb-3" size={22} />
                <div className="font-display font-semibold text-white text-sm mb-1">Aktivera förmåner</div>
                <p className="text-white/60 text-xs leading-relaxed">Skärmrengöring, snabbservice och mer — varje månad.</p>
              </Link>
              <Link to="/access/erbjudanden" className="group rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[var(--color-gold)]/40 hover:bg-white/[0.06] p-6 transition">
                <Tag className="text-[var(--color-gold)] mb-3" size={22} />
                <div className="font-display font-semibold text-white text-sm mb-1">Partnererbjudanden</div>
                <p className="text-white/60 text-xs leading-relaxed">Exklusiva rabatter hos våra samarbetspartners.</p>
              </Link>
              <Link to="/access/bjud-in" className="group rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[var(--color-gold)]/40 hover:bg-white/[0.06] p-6 transition">
                <Gift className="text-[var(--color-gold)] mb-3" size={22} />
                <div className="font-display font-semibold text-white text-sm mb-1">Bjud in en vän</div>
                <p className="text-white/60 text-xs leading-relaxed">Få en månad gratis när din vän blir medlem.</p>
              </Link>
            </div>
          </>
        )}

        {state === "needs_confirm" && (
          <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8 lg:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-display font-semibold mb-5">
              <CheckCircle2 size={14} /> Betalning genomförd
            </div>
            <Mail className="text-[var(--color-gold)] mb-4" size={36} />
            <h1 className="!text-3xl text-white mb-3">Bekräfta din e-post</h1>
            <p className="text-white/75 text-sm leading-relaxed max-w-lg">
              Vi har skickat en bekräftelselänk till {email ? <strong className="text-white">{email}</strong> : "din e-postadress"}. Klicka på länken för att aktivera ditt konto och logga in.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2"><Sparkles size={14} className="text-[var(--color-gold)] mt-1" /> Ditt medlemskap är redan aktivt och betalt.</li>
              <li className="flex items-start gap-2"><Sparkles size={14} className="text-[var(--color-gold)] mt-1" /> Kolla i skräpposten om mejlet inte syns inom 2 minuter.</li>
            </ul>
            <Link to="/access/login" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white font-display font-semibold text-sm transition">
              Logga in <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {state === "needs_login" && (
          <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8 lg:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-display font-semibold mb-5">
              <CheckCircle2 size={14} /> Betalning genomförd
            </div>
            <h1 className="!text-3xl text-white mb-3">Logga in för att komma åt din portal</h1>
            <p className="text-white/75 text-sm leading-relaxed max-w-lg">
              Ditt medlemskap är aktivt. Logga in med din e-post och lösenord för att se dina förmåner.
            </p>
            <Link to="/access/login" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white font-display font-semibold text-sm transition">
              Logga in <ArrowRight size={16} />
            </Link>
          </div>
        )}

        <p className="text-center text-white/40 text-xs mt-10">
          Frågor? Mejla oss på <a href="mailto:info@lagamobilen.se" className="underline hover:text-white/70">info@lagamobilen.se</a>
          {sessionId && <span className="block mt-1 font-mono text-[10px]">Ref: {sessionId.slice(-12)}</span>}
        </p>
      </div>
    </div>
  );
}

export default AccessValkommen;
